const ALLOWED_SVG_TAGS = new Set([
  "svg",
  "g",
  "path",
  "circle",
  "ellipse",
  "line",
  "polyline",
  "polygon",
  "rect",
  "defs",
  "use",
  "title",
  "desc",
]);

/**
 * Validate an SVG markup string before it is injected as HTML.
 *
 * PhotoSwipe's `html` option and Vue's `v-html` both go through innerHTML, so
 * an untrusted value there is a direct XSS sink. Social media data currently
 * lives in a local constant, but this keeps that trust boundary enforced if the
 * data ever comes from a CMS or an API instead.
 *
 * Returns the original markup when it is a single <svg> with no scripts, event
 * handlers or javascript: urls, and an empty string otherwise.
 */
export function toSafeSvg(markup) {
  if (typeof markup !== "string" || !markup.trimStart().startsWith("<svg")) {
    return "";
  }

  const parsed = new DOMParser().parseFromString(markup, "image/svg+xml");
  const root = parsed.documentElement;

  if (!root || root.nodeName === "parsererror" || root.nodeName !== "svg") {
    return "";
  }

  // Include the root itself: querySelectorAll("*") only returns descendants,
  // so <svg onload="..."> would otherwise slip through unchecked
  for (const element of [root, ...root.querySelectorAll("*")]) {
    if (!ALLOWED_SVG_TAGS.has(element.nodeName)) {
      return "";
    }

    for (const attribute of element.attributes) {
      if (/^on/i.test(attribute.name)) {
        return "";
      }
      if (/javascript\s*:/i.test(attribute.value)) {
        return "";
      }
    }
  }

  return markup;
}

const HTML_ESCAPES = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/** Escape a value for interpolation into an HTML string or attribute. */
export function escapeHtml(value) {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
}
